import React, { useEffect, useState } from 'react';

import { Slider, Typography, makeStyles } from '@material-ui/core';
import { buttonInactiveColor, buttonPrimaryColor } from 'theme/theme';
import clsx from 'clsx';

const useStyles = makeStyles(() => ({
  root: {
    color: '#d8d8d8',
    height: 3,
    padding: '13px 0',
    paddingTop: '2rem',
  },
  active: {
    color: buttonPrimaryColor(),
  },
  valueLabel: {
    left: 'calc(-50% + 8px)',
    top: -22,
    '& *': {
      background: 'transparent',
      color: '#000',
    },
  },
  thumb: {
    color: buttonPrimaryColor(),
    height: 27,
    width: 27,
    backgroundColor: '#fff',
    border: '1px solid currentColor',
    marginTop: -12,
    marginLeft: -13,
    boxShadow: '#ebebeb 0 2px 2px',
    '&:focus, &:hover, &$active': {
      boxShadow: '#ccc 0 2px 3px 1px',
    },
    '& .bar': {
      height: 9,
      width: 1,
      backgroundColor: 'currentColor',
      marginLeft: 1,
      marginRight: 1,
    },
  },
  track: {
    height: 3,
  },
  rail: {
    color: '#d8d8d8',
    opacity: 1,
    height: 3,
  },
  sliderContainer: {
    paddingLeft: 13,
    paddingRight: 13,
  },
  sliderContainerDisabled: {
    paddingLeft: 5,
    paddingRight: 5,
  },
  disabledText: {
    color: buttonInactiveColor(),
    height: 0,
  },
}));

const ThumbComponent: React.FC<unknown> = (props) => {
  return (
    <span {...props}>
      {props.children}
      <span className="bar" />
      <span className="bar" />
    </span>
  );
};

interface RangeFilterProps {
  label: string;
  min?: number;
  max?: number;
  step: number;
  value?: number[];
  disabledText?: string;
  onChange?: (value: number[]) => void;
}

const RangeFilter: React.FC<RangeFilterProps> = ({
  label,
  min,
  max,
  step,
  onChange,
  value,
  disabledText,
}) => {
  const classes = useStyles();

  const [storedValue, setValue] = useState<number[] | undefined>();

  const handleChange = (event: unknown, newValue: number | number[]) => {
    setValue(newValue as number[]);
  };

  const handleChangeCommitted = (
    event: unknown,
    newValue: number | number[],
  ) => {
    if (onChange) {
      onChange(newValue as number[]);
    }
  };

  const disabled = min === undefined || max === undefined || min === max;
  const active =
    disabled || !storedValue
      ? false
      : storedValue[0] !== min || storedValue[1] !== max;

  useEffect(() => {
    setValue(value);
  }, [value]);

  // if disabled we show a thumb on each side of the rail, hence the hardcoded [0,1]
  const rmin = disabled ? 0 : (min as number); // disabled = false => min is defined
  const rmax = disabled ? 1 : (max as number); // disabled = false => max is defined
  const rvalue = disabled ? [0, 1] : storedValue ?? [rmin, rmax];

  return (
    <>
      <Typography gutterBottom>{label}</Typography>
      {disabled && disabledText && (
        <div className={classes.disabledText}>{disabledText}</div>
      )}
      <div
        className={
          disabled ? classes.sliderContainerDisabled : classes.sliderContainer
        }
      >
        <Slider
          classes={{
            root: clsx(
              classes.root,
              active !== false ? classes.active : undefined,
            ),
            valueLabel: classes.valueLabel,
            thumb: classes.thumb,
            active: classes.active,
            track: classes.track,
            rail: classes.rail,
          }}
          valueLabelDisplay={active === false ? 'auto' : 'on'}
          ThumbComponent={!disabled ? ThumbComponent : undefined} // custom thumb is ugly in disabled mode
          onChangeCommitted={handleChangeCommitted}
          onChange={handleChange}
          value={rvalue}
          min={rmin}
          max={rmax}
          step={step ?? 1}
          disabled={disabled}
        />
      </div>
    </>
  );
};

export default RangeFilter;
